// src/contexts/UserContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';

const UserContext = createContext();

/* ========== API base (REACT_APP_API_BASE only) ========== */

/* ===== API base (Vite compatible) ===== */
const ENV_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_REACT_APP_API_BASE) ||   // ✅ same pattern as TAX
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE) ||             // optional backup
  process.env.REACT_APP_API_BASE ||               // fallback if ever CRA
  "";

const CLEAN_ENV_BASE = String(ENV_BASE || "").replace(/\/+$/, "");
const FALLBACK_BASE = "https://www.doroosonline.co.il";
const API_BASE = CLEAN_ENV_BASE || FALLBACK_BASE;

const url = (path) =>
  `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;

const DEFAULT_TIMEOUT_MS = 15000;


/* ========== helpers ========== */

async function parseJSONSafely(res) {
  if (res.status === 204) return {};
  const text = await res.text();
  if (!text) return {};
  try { return JSON.parse(text); } catch { return { raw: text }; }
}

async function toJSON(res) {
  const data = await parseJSONSafely(res);
  if (!res.ok) {
    const msg =
      data?.error ||
      data?.message ||
      (res.status === 401 ? 'שגיאה בהרשאות (401)' : `שגיאת שרת (${res.status})`);
    const err = new Error(msg);
    err.status = res.status;
    err.payload = data;
    throw err;
  }
  return data ?? {};
}

function normalizeUser(u) {
  if (!u) return null;
  const fullNameFromParts = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  const fullName = u.full_name || fullNameFromParts;

  return {
    id: u.id,
    full_name: u.full_name || '',
    first_name: u.first_name ?? '',
    last_name: u.last_name ?? '',
    email_confirmed: Number(u.email_confirmed ?? u.e_confirmed ?? 0),
    phone_confirmed: Number(u.phone_confirmed ?? u.m_confirmed ?? 0),

    fullName: fullName || '',
    firstName: u.first_name ?? '',
    lastName: u.last_name ?? '',
    email: u.email ?? '',
    phone: u.phone ?? null,

    // ✅ allow admin role (backend must return role: "admin")
    role: u.role ?? 'student',

    picture: u.picture ?? u.picture_url ?? '',
    emailConfirmed: Number(u.email_confirmed ?? u.e_confirmed ?? 0),
    phoneConfirmed: Number(u.phone_confirmed ?? u.m_confirmed ?? 0),
    aboutMe: u.about_me ?? u.aboutMe ?? '',
    department: u.department ?? '',

    zoomAccountId: u.zoom_account_id ?? u.zoomAccountId ?? '',
    zoomClientId:  u.zoom_client_id  ?? u.zoomClientId  ?? '',
    zoomSecretKey: u.zoom_secret_key ?? u.zoomSecretKey ?? '',

    // ✅ ADD THESE
    teacher_status: u.teacher_status ?? null,
    rating: Number(u.rating ?? 0),
    rating_count: Number(u.rating_count ?? 0),
    // Learning-center average of students' lesson ratings (from /users/me)
    supplierRating: Number(u.supplier_rating ?? 0),
    supplierRatingCount: Number(u.supplier_rating_count ?? 0),

    // Learning-center (supplier) approval status, mirrors teacher_status.
    // Comes from supplier_profiles.status — the backend's /api/users/me
    // needs to LEFT JOIN supplier_profiles and alias its `status` column
    // as `supplier_status` in the row it returns for a role='supplier'
    // user, the same way teacher_status already lives directly on
    // `users`. Until that join exists server-side this stays null and
    // course creation for suppliers stays gated off (fails safe).
    supplier_status: u.supplier_status ?? null,

    // The supplier's actual business/learning-center name, from
    // supplier_profiles.supplier_name — now that UsersController::me()
    // selects it in the same join. This is what
    // PersonalInfoSection.jsx reads/saves for a supplier instead of
    // firstName/lastName.
    supplier_name: u.supplier_name ?? null,
    businessName: u.supplier_name ?? null,
    // Supplier's contact person (supplier_profiles.contact_person).
    contactPerson: u.supplier_contact_person ?? u.contactPerson ?? '',

    // School profile fields, from school_profiles via UsersController's
    // join (school_name / school_symbol AS institution_code /
    // contact_person AS principal_name). PersonalInfoSection.jsx reads
    // schoolName/principalName/institutionCode for role === 'school'.
    schoolName: u.school_name ?? u.schoolName ?? '',
    principalName: u.principal_name ?? u.principalName ?? '',
    institutionCode: u.institution_code ?? u.institutionCode ?? '',

    // ✅ NEW
    credit_balance: Number(u.credit_balance ?? 0),
    creditBalance: Number(u.credit_balance ?? 0),

    created_at: u.created_at,
    updated_at: u.updated_at,
  };
}

/* ========= token helpers + "authFetch" ========= */
const TOKEN_STORAGE_KEYS = ['token', 'jwt', 'JWT', 'accessToken', 'authToken'];

const getStoredToken = () => {
  try {
    if (typeof window === 'undefined') return null;
    for (const k of TOKEN_STORAGE_KEYS) {
      const v = localStorage.getItem(k) || sessionStorage.getItem(k);
      if (v) return v;
    }
  } catch (_) {}
  return null;
};

async function apiFetch(
  path,
  { method = 'GET', body, headers = {}, timeout = DEFAULT_TIMEOUT_MS, onUnauthorized } = {}
) {
  const token = getStoredToken();
  const hasBody = body !== undefined;

  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeout);

  try {
    const res = await fetch(url(path), {
      method,
      headers: {
        ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      body: hasBody ? JSON.stringify(body) : undefined,
      mode: 'cors',
      cache: 'no-store',
      credentials: 'omit',
      signal: controller.signal,
    });

    if (res.status === 401 && typeof onUnauthorized === 'function') {
      onUnauthorized();
    }
    return await toJSON(res);
  } catch (e) {
    if (e.name === 'AbortError') throw new Error('זמן ההמתנה לשרת הסתיים. נסה שוב בעוד רגע.');
    if (e instanceof TypeError) {
      const hint = API_BASE
        ? `בדוק שהשרת זמין ב- ${API_BASE} וש-CORS מאפשר את המקור של הלקוח.`
        : 'הגדר VITE_API_BASE/REACT_APP_API_BASE כדי לוודא שהלקוח פונה לשרת הנכון.';
      throw new Error(`שגיאת רשת: לא ניתן להגיע לשרת. ${hint}`);
    }
    throw e;
  } finally {
    clearTimeout(t);
  }
}

async function authFetch(
  path,
  { method = 'GET', body, onUnauthorized, timeout = DEFAULT_TIMEOUT_MS } = {}
) {
  const token = getStoredToken();
  if (!token) throw new Error('נדרשת התחברות (חסר טוקן)');

  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeout);

  try {
    const res = await fetch(url(path), {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      mode: 'cors',
      credentials: 'omit',
      cache: 'no-store',
      signal: controller.signal,
    });

    const data = await parseJSONSafely(res);
    if (!res.ok || (data && data.ok === false)) {
      if (res.status === 401 && typeof onUnauthorized === 'function') {
        try { onUnauthorized(); } catch {}
      }
      const msg = data?.error || data?.message || `שגיאת שרת (${res.status})`;
      throw new Error(msg);
    }
    return data ?? {};
  } catch (e) {
    if (e.name === 'AbortError') throw new Error('זמן ההמתנה לשרת הסתיים. נסה שוב בעוד רגע.');
    throw e;
  } finally {
    clearTimeout(t);
  }
}

const extractApiError = (err) => {
  const status = err?.status;
  const payload = err?.payload;

  const msg =
    payload?.error ||
    payload?.message ||
    err?.message ||
    'אירעה שגיאה לא צפויה';

  return { status, payload, msg };
};

/* ===================== Role guard helpers ===================== */
const roleLabelHe = (r) =>
  r === 'teacher' ? 'מורים' :
  r === 'student' ? 'תלמידים' :
  r === 'admin'   ? 'מנהל מערכת' :
  r;

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const token = getStoredToken();
    if (!token) return null;
    const saved = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
    return saved ? normalizeUser(JSON.parse(saved)) : null;
  });

  const [userType, setUserType] = useState(() => {
    const token = getStoredToken();
    if (!token) return null;
    return typeof window !== 'undefined' ? localStorage.getItem('userType') || null : null;
  });

  const [role, setRole] = useState(() => {
    const token = getStoredToken();
    if (!token) return 'default';
    return typeof window !== 'undefined' ? localStorage.getItem('role') || 'default' : 'default';
  });

  const [ready, setReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  const clearAlert = useCallback(() => setAlert(null), []);

  const showAlert = useCallback((message, type = 'error') => {
    setAlert({ message, type });

    window.clearTimeout(window.__userAlertTimer);
    window.__userAlertTimer = window.setTimeout(() => setAlert(null), 6000);
  }, []);

  const hardLogout = useCallback(() => {
    try {
      TOKEN_STORAGE_KEYS.forEach((k) => {
        localStorage.removeItem(k);
        sessionStorage.removeItem(k);
      });
      localStorage.removeItem('user');
      localStorage.removeItem('role');
      localStorage.removeItem('userType');
    } catch (_) {}

    setUser(null);
    setRole('default');
    setUserType(null);
  }, []);

  // restore session
  useEffect(() => {
    (async () => {
      const token = getStoredToken();
      if (!token) {
        hardLogout();
        setReady(true);
        return;
      }
      try {
        const data = await apiFetch('/api/users/me', { onUnauthorized: hardLogout });
        if (data?.user) {
          const normalized = normalizeUser(data.user);
          setUser(normalized);
          setRole(normalized.role || 'default');
          localStorage.setItem('user', JSON.stringify(normalized));
          if (normalized.role) localStorage.setItem('role', normalized.role);
          if (!localStorage.getItem('userType') && normalized.role) {
            localStorage.setItem('userType', normalized.role);
            setUserType(normalized.role);
          }
        }
      } catch {
        hardLogout();
      } finally {
        setReady(true);
      }
    })();
  }, [hardLogout]);

  // sync tabs
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'token' || e.key === 'user' || e.key === 'role' || e.key === 'userType') {
        const token = getStoredToken();
        const newUser = token ? localStorage.getItem('user') : null;
        const newRole = token ? (localStorage.getItem('role') || 'default') : 'default';
        const newUserType = token ? (localStorage.getItem('userType') || null) : null;
        setUser(token && newUser ? normalizeUser(JSON.parse(newUser)) : null);
        setRole(newRole);
        setUserType(newUserType);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  /* ===================== Auth APIs ===================== */
  // ✅ login: add roleOverride
  const login = useCallback(
    async (email, password, auth = 'normal', roleOverride = null) => {

      setIsLoading(true);
      try {

        const effectiveType = roleOverride || userType || 'student';

        const data = await apiFetch('/api/auth/login', {
          method: 'POST',
          body: { email, password, auth, role: effectiveType },
        });

        const token = data?.token;
        const me = normalizeUser(data?.user);
        if (!token || !me) throw new Error('תשובת התחברות לא תקינה');

        // ✅ HARD ROLE ENFORCEMENT
        if ((me.role || 'student') !== effectiveType) {
          hardLogout();
          throw new Error("שם משתמש או סיסמה שגויים.");
        }

        try {
          localStorage.setItem('token', token);
          localStorage.setItem('jwt', token);
        } catch (_) {}

        // The login payload is a trimmed user (no supplier_status etc.).
        // Pull the full profile so approval-gated UI is right immediately,
        // not only after a page refresh. Best-effort: fall back to `me`.
        let fullMe = me;
        try {
          const meData = await apiFetch('/api/users/me', { onUnauthorized: () => {} });
          if (meData?.user) fullMe = { ...me, ...normalizeUser(meData.user) };
        } catch (_) {}

        localStorage.setItem('user', JSON.stringify(fullMe));
        localStorage.setItem('role', fullMe.role || effectiveType || 'default');
        localStorage.setItem('userType', fullMe.role || effectiveType);

        setUser(fullMe);
        setRole(fullMe.role || effectiveType || 'default');
        setUserType(fullMe.role || effectiveType);
      } catch (err) {
          showAlert(err.message || 'התחברות נכשלה');

        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [showAlert, userType, hardLogout]
  );

  // ✅ signup: add roleOverride
  const signup = useCallback(
    async (
      { fullName, firstName, lastName, email, picture = '', password, auth = 'normal' },
      roleOverride = null
    ) => {
      setIsLoading(true);
      try {
        const effectiveType = roleOverride || userType || 'student';

        const body = fullName
          ? { fullName, email, password, picture, auth, role: effectiveType }
          : { firstName, lastName, email, password, picture, auth, role: effectiveType };

        const data = await apiFetch('/api/auth/signup', {
          method: 'POST',
          body,
        });

        const token = data?.token;
        const me = normalizeUser(data?.user);
        if (!token || !me) throw new Error('תשובת הרשמה לא תקינה');

        // ✅ HARD ROLE ENFORCEMENT
        if ((me.role || 'student') !== effectiveType) {
          hardLogout();
          throw new Error(
            `לא ניתן להירשם. השרת החזיר role=${me.role} אך במסך זה נבחר ${effectiveType}.`
          );
        }

        try {
          localStorage.setItem('token', token);
          localStorage.setItem('jwt', token);
        } catch (_) {}

        localStorage.setItem('user', JSON.stringify(me));
        localStorage.setItem('role', me.role || effectiveType || 'default');
        localStorage.setItem('userType', me.role || effectiveType);

        setUser(me);
        setRole(me.role || effectiveType || 'default');
        setUserType(me.role || effectiveType);
      } catch (err) {
        showAlert(err.message || 'הרשמה נכשלה');
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [showAlert, userType, hardLogout]
  );

  // ✅ socialLogin: pass roleOverride through
  const socialLogin = useCallback(
    async (mode, payload, roleOverride = null) => {
      const { password, fullName, firstName, lastName, email, picture, auth } = payload;
      if (mode === 'signup') return signup({ fullName, firstName, lastName, email, password, picture, auth }, roleOverride);
      if (mode === 'login') return login(email, password, auth, roleOverride);
      throw new Error('Invalid mode for social login');
    },
    [login, signup]
  );

  const logout = useCallback(async () => {
    setIsLoading(true);

    try {
      const token = getStoredToken();

      if (token) {
        await apiFetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: 8000,
        });
      }

      hardLogout();
      showAlert('ההתנתקות בוצעה בהצלחה', 'success');
      return { success: true };
    } catch (err) {
      hardLogout();
      showAlert(err?.message || 'ההתנתקות בוצעה מקומית, אך לא הושלמה בשרת', 'error');
      return {
        success: false,
        error: err?.message || 'ההתנתקות בוצעה מקומית, אך לא הושלמה בשרת',
      };
    } finally {
      setIsLoading(false);
    }
  }, [hardLogout, showAlert]);

  /* ================ Profile & account APIs ================ */
  const updateUserProfile = useCallback(async (updatedFields) => {
    if (!user) throw new Error('אין משתמש מחובר');
    setIsLoading(true);
    try {
      const body = {
        ...(updatedFields.firstName     !== undefined ? { first_name:       updatedFields.firstName }     : {}),
        ...(updatedFields.lastName      !== undefined ? { last_name:        updatedFields.lastName }      : {}),
        ...(updatedFields.email         !== undefined ? { email:            updatedFields.email }         : {}),
        ...(updatedFields.phone         !== undefined ? { phone:            updatedFields.phone }         : {}),
        ...(updatedFields.picture       !== undefined ? { picture:          updatedFields.picture }       : {}),
        ...(updatedFields.role          !== undefined ? { role:             updatedFields.role }          : {}),
        ...(updatedFields.aboutMe       !== undefined ? { about_me:         updatedFields.aboutMe }       : {}),
        ...(updatedFields.department    !== undefined ? { department:       updatedFields.department }    : {}),
        ...(updatedFields.zoomAccountId !== undefined ? { zoom_account_id:  updatedFields.zoomAccountId } : {}),
        ...(updatedFields.zoomClientId  !== undefined ? { zoom_client_id:   updatedFields.zoomClientId }  : {}),
        ...(updatedFields.zoomSecretKey !== undefined ? { zoom_secret_key:  updatedFields.zoomSecretKey } : {}),
        // Supplier-only: business name, saved to supplier_profiles.
        // supplier_name server-side (see UsersController::update()),
        // not the `users` table at all.
        ...(updatedFields.businessName  !== undefined ? { business_name:    updatedFields.businessName }  : {}),
        ...(updatedFields.contactPerson !== undefined ? { contact_person:   updatedFields.contactPerson } : {}),
        // School-only fields, saved to school_profiles (see
        // UsersController::update()). These are camelCase with no
        // underscore, so without an explicit line here they were being
        // silently dropped by the generic k.includes('_') fallback below
        // — the form submitted fine, but the request body never carried
        // schoolName/principalName/institutionCode to the server.
        ...(updatedFields.schoolName      !== undefined ? { school_name:      updatedFields.schoolName }      : {}),
        ...(updatedFields.principalName   !== undefined ? { principal_name:   updatedFields.principalName }   : {}),
        ...(updatedFields.institutionCode !== undefined ? { institution_code: updatedFields.institutionCode } : {}),
        ...Object.fromEntries(Object.entries(updatedFields).filter(([k]) => k.includes('_'))),
      };

      const data = await apiFetch('/api/users/me', {
        method: 'PATCH',
        body,
        onUnauthorized: hardLogout,
      });

      const updated = normalizeUser(data?.user) || { ...user, ...updatedFields };
      setUser(updated);
      localStorage.setItem('user', JSON.stringify(updated));
      return { success: true, data: updated };
    } catch (err) {
      showAlert(err.message || 'שגיאה בעדכון פרופיל המשתמש');
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, [hardLogout, showAlert, user]);

  const changeUserPassword = useCallback((currentPassword, newPassword) =>
    new Promise(async (resolve, reject) => {
      if (!user) return reject('אין משתמש מחובר כרגע');
      try {
        await apiFetch('/api/users/me/password', {
          method: 'POST',
          body: { currentPassword, newPassword },
          onUnauthorized: hardLogout,
        });
        resolve('הסיסמה עודכנה בהצלחה');
      } catch (err) {
        reject(err.message || 'שגיאה בעדכון סיסמה');
      }
    }), [hardLogout, user]);

  const sendVerificationCode = useCallback(async (field, value, opts = {}) => {
    if (!user) throw new Error('אין משתמש מחובר');
    if (field !== 'email' && field !== 'phone') throw new Error('שדה אימות לא תקין');

    let v = (value ?? '').toString().trim();
    if (!v) throw new Error(field === 'email' ? 'נא להזין כתובת אימייל' : 'נא להזין מספר טלפון');
    if (field === 'phone') v = v.replace(/[\s-]/g, '');

    const data = await authFetch('/api/users/me/verify/send', {
      method: 'POST',
      body: { field, value: v },
      onUnauthorized: hardLogout,
      ...(opts?.timeout ? { timeout: opts.timeout } : {}),
    });
    return data || { ok: true };
  }, [hardLogout, user]);

  const checkVerificationCode = useCallback(async (field, code, nonce, opts = {}) => {
    if (!user) throw new Error('אין משתמש מחובר');
    const body = { field, code, ...(nonce ? { nonce } : {}) };

    const data = await apiFetch('/api/users/me/verify/check', {
      method: 'POST',
      body,
      onUnauthorized: hardLogout,
      ...(opts?.timeout ? { timeout: opts.timeout } : {}),
    });
    if (data?.user) {
      const normalized = normalizeUser(data.user);
      setUser(normalized);
      localStorage.setItem('user', JSON.stringify(normalized));
    }
    return data;
  }, [hardLogout, user]);

  const updateConfirmationStatus = useCallback(async (field, value) => {
    if (!user) throw new Error('אין משתמש מחובר');
    const data = await apiFetch('/api/users/me/confirm', {
      method: 'PATCH',
      body: { field, value },
      onUnauthorized: hardLogout,
    });
    if (data?.user) {
      const normalized = normalizeUser(data.user);
      setUser(normalized);
      localStorage.setItem('user', JSON.stringify(normalized));
    }
  }, [hardLogout, user]);

  const updateUserPicture = useCallback(async (newPictureUrl) => {
    if (!user) return;
    const data = await apiFetch('/api/users/me/picture', {
      method: 'PATCH',
      body: { picture: newPictureUrl },
      onUnauthorized: hardLogout,
    });
    const updated = normalizeUser(data?.user) || { ...user, picture: newPictureUrl };
    setUser(updated);
    localStorage.setItem('user', JSON.stringify(updated));
  }, [hardLogout, user]);

  const listUserPhotos = useCallback(async () => {
    const data = await authFetch('/api/users/me/photos', {
      method: 'GET',
      onUnauthorized: hardLogout,
    });
    return data?.photos || [];
  }, [hardLogout]);

  const addUserPhotoRecord = useCallback(async ({ id, file_name, url: photoUrl }) => {
    const data = await authFetch('/api/users/me/photos', {
      method: 'POST',
      body: { id, file_name, url: photoUrl },
      onUnauthorized: hardLogout,
    });
    return data?.photo;
  }, [hardLogout]);

  const deleteUserPhotoRecord = useCallback(async (photoId) => {
    await authFetch(`/api/users/me/photos/${encodeURIComponent(photoId)}`, {
      method: 'DELETE',
      onUnauthorized: hardLogout,
    });
  }, [hardLogout]);

  const setUserTypePersist = useCallback((type) => {
    setUserType(type);
    localStorage.setItem('userType', type);
  }, []);

  /* ===================== Contact (public) ===================== */
  const sendContactMessage = useCallback(async (payload) => {
    const data = await apiFetch('/api/users/contact', {
      method: 'POST',
      body: payload,
      onUnauthorized: hardLogout,
      timeout: 15000,
    });
    return data;
  }, [hardLogout]);

  /* ===================== Forgot Password (public) ===================== */
  const requestPasswordReset = useCallback(async (email) => {
    setIsLoading(true);
    try {
      const data = await apiFetch('/api/auth/forgot', {
        method: 'POST',
        body: { email },
        timeout: 15000,
      });
      return data;
    } catch (err) {
      const { msg } = extractApiError(err);
      throw new Error(msg || 'שגיאה בשליחת קישור שחזור');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const resetPassword = useCallback(async ({ email, token, newPassword }) => {
    setIsLoading(true);
    try {
      const data = await apiFetch('/api/auth/reset', {
        method: 'POST',
        body: { email, token, newPassword },
        timeout: 15000,
      });

      return data;
    } catch (err) {
      const { status, payload, msg } = extractApiError(err);

      if (status === 422 && payload?.errors) {
        const e = new Error('VALIDATION_ERROR');
        e.validationErrors = payload.errors;
        throw e;
      }

      throw new Error(msg || 'שגיאה באיפוס הסיסמה');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshMe = useCallback(async () => {
    const token = getStoredToken();
    if (!token) return null;

    const data = await apiFetch('/api/users/me', { onUnauthorized: hardLogout });
    if (data?.user) {
      const normalized = normalizeUser(data.user);
      setUser(normalized);
      localStorage.setItem('user', JSON.stringify(normalized));
      if (normalized.role) localStorage.setItem('role', normalized.role);
      return normalized;
    }
    return null;
  }, [hardLogout]);


  const updateLocalCreditBalance = useCallback((newBalance) => {
    const safeBalance = Number(newBalance ?? 0);

    setUser((prev) => {
      if (!prev) return prev;

      const updated = {
        ...prev,
        credit_balance: safeBalance,
        creditBalance: safeBalance,
      };

      try {
        localStorage.setItem('user', JSON.stringify(updated));
      } catch (_) {}

      return updated;
    });
  }, []);


  const value = useMemo(
    () => ({
      ready,
      user,
      login,
      signup,
      logout,
      isLogedin: !!user,

      updateUserProfile,
      changeUserPassword,
      requestPasswordReset,
      resetPassword,
      refreshMe,

      updateConfirmationStatus,
      sendVerificationCode,
      checkVerificationCode,

      setUserType: setUserTypePersist,
      userType,
      role,
      socialLogin,

      updateUserPicture,

      listUserPhotos,
      addUserPhotoRecord,
      deleteUserPhotoRecord,
      sendContactMessage,

      // ✅ NEW
      updateLocalCreditBalance,

      isLoading,
      alert,
      showAlert,
      clearAlert,
      setAlert,
    }),
    [
      ready,
      user,
      userType,
      role,
      isLoading,
      alert,
      login,
      signup,
      logout,
      updateUserProfile,
      changeUserPassword,
      requestPasswordReset,
      resetPassword,
      refreshMe,
      updateConfirmationStatus,
      sendVerificationCode,
      checkVerificationCode,
      setUserTypePersist,
      socialLogin,
      updateUserPicture,
      listUserPhotos,
      addUserPhotoRecord,
      deleteUserPhotoRecord,
      sendContactMessage,
      updateLocalCreditBalance,
      showAlert,
      setAlert,
      clearAlert,
    ]
  );

  if (typeof window !== 'undefined' && !process.env.REACT_APP_API_BASE) {
    console.warn(
      `[UserContext] REACT_APP_API_BASE not set in env. Falling back to ${FALLBACK_BASE}.`
    );
  }

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error('useUser must be used within a UserProvider');
  return ctx;
};